import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Radio,
  Checkbox,
  Spin,
  Alert,
  Typography,
  Space,
  Divider,
  Tag,
  message,
  Empty,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ForwardOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { pendingUpdatesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

// Custom styles for decision buttons
const decisionButtonStyles = `
  .decision-btn-group .ant-radio-button-wrapper {
    border-radius: 4px !important;
    margin-right: 4px;
    border: 1px solid #d9d9d9;
  }
  .decision-btn-group .ant-radio-button-wrapper:not(:first-child)::before {
    display: none;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.accept-btn {
    background-color: #52c41a !important;
    border-color: #52c41a !important;
    color: #fff !important;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.accept-btn:hover {
    background-color: #73d13d !important;
    border-color: #73d13d !important;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.reject-btn {
    background-color: #ff4d4f !important;
    border-color: #ff4d4f !important;
    color: #fff !important;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.reject-btn:hover {
    background-color: #ff7875 !important;
    border-color: #ff7875 !important;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.skip-btn {
    background-color: #faad14 !important;
    border-color: #faad14 !important;
    color: #fff !important;
  }
  .decision-btn-group .ant-radio-button-wrapper-checked.skip-btn:hover {
    background-color: #ffc53d !important;
    border-color: #ffc53d !important;
  }
  .decision-btn-group .accept-btn:not(.ant-radio-button-wrapper-checked) {
    color: #52c41a;
  }
  .decision-btn-group .accept-btn:not(.ant-radio-button-wrapper-checked):hover {
    color: #73d13d;
    border-color: #52c41a;
  }
  .decision-btn-group .reject-btn:not(.ant-radio-button-wrapper-checked) {
    color: #ff4d4f;
  }
  .decision-btn-group .reject-btn:not(.ant-radio-button-wrapper-checked):hover {
    color: #ff7875;
    border-color: #ff4d4f;
  }
  .decision-btn-group .skip-btn:not(.ant-radio-button-wrapper-checked) {
    color: #faad14;
  }
  .decision-btn-group .skip-btn:not(.ant-radio-button-wrapper-checked):hover {
    color: #ffc53d;
    border-color: #faad14;
  }
`;

// Types
interface EntityUpdate {
  entityType: 'SHIPMENT' | 'CONTAINER' | 'GOOD' | 'BOOKING' | 'CONTRACT';
  entityId: string;
  proposedChanges: Record<string, unknown>;
  currentValues: Record<string, unknown>;
}

interface PendingUpdate {
  id: string;
  updates: EntityUpdate[];
  sourceType: string;
  sourceId: string | null;
  sourceDocumentUrl: string | null;
  blockedAction: string | null;
  blockedActionPayload: Record<string, unknown> | null;
  status: string;
  createdAt: string;
  shipmentId: string | null;
  contractId: string | null;
}

interface NextResponse {
  pendingUpdates: PendingUpdate[];
  shipment: {
    id: string;
    reference: string;
    contract: {
      id: string;
      reference: string;
      buyer: { id: string; name: string; code: string | null } | null;
      supplier: { id: string; name: string; code: string | null } | null;
    } | null;
  } | null;
  contract: {
    id: string;
    reference: string;
    buyer: { id: string; name: string; code: string | null } | null;
    supplier: { id: string; name: string; code: string | null } | null;
  } | null;
  totalPending: number;
}

type FieldDecision = 'accept' | 'reject' | 'skip' | null;

interface FieldState {
  decision: FieldDecision;
  editedValue: unknown;
}

// Human-readable field names
const FIELD_LABELS: Record<string, string> = {
  containerNumber: 'Container Number',
  sealNumber: 'Seal Number',
  weight: 'Weight (kg)',
  netWeight: 'Net Weight (kg)',
  grossWeight: 'Gross Weight (kg)',
  eta: 'ETA',
  etd: 'ETD',
  ata: 'ATA',
  atd: 'ATD',
  vesselName: 'Vessel Name',
  voyageNumber: 'Voyage Number',
  pol: 'Port of Loading',
  pod: 'Port of Discharge',
  bookingNumber: 'Booking Number',
  blNumber: 'B/L Number',
  quantity: 'Quantity',
  quantityUnit: 'Quantity Unit',
  commodity: 'Commodity',
  description: 'Description',
  cyCutDate: 'CY Cut Date',
  docCutDate: 'Document Cut Date',
};

// Human-readable blocked action names
const BLOCKED_ACTION_LABELS: Record<string, string> = {
  CREATE_XERO_INVOICE: 'Create Customer Invoice in Xero',
  CREATE_COMMISSION_INVOICE: 'Create Commission Invoice in Xero',
  SEND_SHIPPING_INSTRUCTION: 'Send Shipping Instruction',
  UPDATE_PORTCAST: 'Update Portcast Tracking',
  NONE: 'None',
};

// Normalize value to match what will be saved to database
const normalizeForDatabase = (value: unknown, fieldName: string, entityType: string): unknown => {
  if (value === null || value === undefined) return value;

  // Container numbers: remove hyphens, spaces, and other non-alphanumeric characters
  if (entityType === 'CONTAINER' && fieldName === 'containerNumber' && typeof value === 'string') {
    return value.replace(/[-\s]/g, '').toUpperCase();
  }

  return value;
};

// Format value for display
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Get entity type label
const getEntityLabel = (entityType: string): string => {
  const labels: Record<string, string> = {
    SHIPMENT: 'Shipment',
    CONTAINER: 'Container',
    GOOD: 'Good',
    BOOKING: 'Booking',
    CONTRACT: 'Contract',
  };
  return labels[entityType] || entityType;
};

// Field row component
interface FieldRowProps {
  entityType: string;
  entityId: string;
  fieldName: string;
  currentValue: unknown;
  proposedValue: unknown;
  state: FieldState;
  onChange: (decision: FieldDecision, editedValue?: unknown) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({
  entityType,
  fieldName,
  currentValue,
  proposedValue,
  state,
  onChange,
}) => {
  const label = FIELD_LABELS[fieldName] || fieldName;
  const [editMode, setEditMode] = useState(false);

  // Normalize the proposed value to show what will actually be saved to DB
  const normalizedProposedValue = normalizeForDatabase(proposedValue, fieldName, entityType);
  const [localValue, setLocalValue] = useState(formatValue(normalizedProposedValue));

  const handleDecisionChange = (decision: FieldDecision) => {
    // Always use normalized value when accepting
    onChange(decision, decision === 'accept' ? (editMode ? localValue : normalizedProposedValue) : undefined);
  };

  const handleValueChange = (newValue: string) => {
    setLocalValue(newValue);
    if (state.decision === 'accept') {
      onChange('accept', newValue);
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setLocalValue(formatValue(normalizedProposedValue));
    }
  };

  return (
    <div className="field-row">
      <div className="field-label">
        <Text strong>{label}</Text>
      </div>
      <div className="field-values">
        <div className="field-current">
          <Text type="secondary" style={{ fontSize: 11 }}>Current</Text>
          <Text>{formatValue(currentValue)}</Text>
        </div>
        <div className="field-arrow">→</div>
        <div className="field-proposed">
          <Text type="secondary" style={{ fontSize: 11 }}>
            Proposed
            {state.decision === 'accept' && (
              <Button
                type="link"
                size="small"
                onClick={handleEditToggle}
                style={{ padding: '0 4px', fontSize: 11 }}
              >
                {editMode ? 'Reset' : 'Edit'}
              </Button>
            )}
          </Text>
          {editMode && state.decision === 'accept' ? (
            <Input
              size="small"
              value={localValue}
              onChange={(e) => handleValueChange(e.target.value)}
              style={{ width: '100%' }}
            />
          ) : (
            <Text
              style={{
                color: state.decision === 'reject' ? '#999' : undefined,
                textDecoration: state.decision === 'reject' ? 'line-through' : undefined,
              }}
            >
              {state.editedValue !== undefined ? formatValue(state.editedValue) : formatValue(normalizedProposedValue)}
            </Text>
          )}
        </div>
      </div>
      <div className="field-actions">
        <Radio.Group
          value={state.decision}
          onChange={(e) => handleDecisionChange(e.target.value)}
          buttonStyle="solid"
          size="small"
          className="decision-btn-group"
        >
          <Tooltip title="Accept this change">
            <Radio.Button value="accept" className="accept-btn">
              <CheckOutlined /> Accept
            </Radio.Button>
          </Tooltip>
          <Tooltip title="Reject and delete this change">
            <Radio.Button value="reject" className="reject-btn">
              <CloseOutlined /> Reject
            </Radio.Button>
          </Tooltip>
          <Tooltip title="Skip and review later">
            <Radio.Button value="skip" className="skip-btn">
              <ForwardOutlined /> Skip
            </Radio.Button>
          </Tooltip>
        </Radio.Group>
      </div>
    </div>
  );
};

// Main component
const PendingUpdatesReview: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Field decisions state: Map<"entityType:entityId:fieldName", FieldState>
  const [fieldStates, setFieldStates] = useState<Map<string, FieldState>>(new Map());
  const [skipBlockedAction, setSkipBlockedAction] = useState(false);

  // Fetch next pending update(s)
  // Note: api interceptor already unwraps response.data, so we get the data directly
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pending-updates-next'],
    queryFn: async () => {
      // The interceptor returns data directly, but TypeScript thinks it's AxiosResponse
      const result = await pendingUpdatesApi.getNext() as unknown as NextResponse;
      return result;
    },
    retry: false,
  });

  // Process mutation
  const processMutation = useMutation({
    mutationFn: async (pendingUpdateId: string) => {
      const decisions = Array.from(fieldStates.entries())
        .filter(([key]) => key.startsWith(`${pendingUpdateId}:`))
        .map(([key, state]) => {
          // Key format: puId:entityType:entityId:fieldName
          // Split carefully - entityId is a CUID and fieldName could have colons
          const withoutPuId = key.slice(pendingUpdateId.length + 1);
          const firstColon = withoutPuId.indexOf(':');
          const entityType = withoutPuId.slice(0, firstColon);
          const rest = withoutPuId.slice(firstColon + 1);
          const lastColon = rest.lastIndexOf(':');
          const entityId = rest.slice(0, lastColon);
          const fieldName = rest.slice(lastColon + 1);

          return {
            entityType,
            entityId,
            fieldName,
            decision: state.decision as 'accept' | 'reject' | 'skip',
            editedValue: state.decision === 'accept' ? state.editedValue : undefined,
          };
        });

      return pendingUpdatesApi.process(pendingUpdateId, {
        fieldDecisions: decisions,
        skipBlockedAction,
        reviewedBy: user?.email || 'unknown',
      });
    },
    onSuccess: () => {
      message.success('Changes processed successfully');
      setFieldStates(new Map());
      setSkipBlockedAction(false);
      queryClient.invalidateQueries({ queryKey: ['pending-updates-next'] });
      queryClient.invalidateQueries({ queryKey: ['pending-updates-count'] });
    },
    onError: (err: Error) => {
      message.error(`Failed to process: ${err.message}`);
    },
  });

  // Build field key
  const getFieldKey = (puId: string, entityType: string, entityId: string, fieldName: string) => {
    return `${puId}:${entityType}:${entityId}:${fieldName}`;
  };

  // Handle field decision change
  const handleFieldChange = (
    puId: string,
    entityType: string,
    entityId: string,
    fieldName: string,
    decision: FieldDecision,
    editedValue?: unknown
  ) => {
    const key = getFieldKey(puId, entityType, entityId, fieldName);
    setFieldStates((prev) => {
      const next = new Map(prev);
      next.set(key, { decision, editedValue });
      return next;
    });
  };

  // Get all fields from all pending updates
  const allFields = useMemo(() => {
    if (!data?.pendingUpdates) return [];

    const fields: Array<{
      puId: string;
      entityType: string;
      entityId: string;
      fieldName: string;
      currentValue: unknown;
      proposedValue: unknown;
    }> = [];

    for (const pu of data.pendingUpdates) {
      for (const update of pu.updates) {
        for (const fieldName of Object.keys(update.proposedChanges)) {
          fields.push({
            puId: pu.id,
            entityType: update.entityType,
            entityId: update.entityId,
            fieldName,
            currentValue: update.currentValues[fieldName],
            proposedValue: update.proposedChanges[fieldName],
          });
        }
      }
    }

    return fields;
  }, [data]);

  // Group fields by entity
  const groupedFields = useMemo(() => {
    const groups: Map<string, typeof allFields> = new Map();

    for (const field of allFields) {
      const key = `${field.entityType}:${field.entityId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(field);
    }

    return groups;
  }, [allFields]);

  // Check if all fields have decisions
  const allFieldsDecided = useMemo(() => {
    return allFields.every((f) => {
      const key = getFieldKey(f.puId, f.entityType, f.entityId, f.fieldName);
      const state = fieldStates.get(key);
      return state?.decision != null;
    });
  }, [allFields, fieldStates]);

  // Handle submit
  const handleSubmit = async () => {
    if (!allFieldsDecided) {
      message.warning('Please make a decision for all fields before submitting');
      return;
    }

    // Process each pending update
    for (const pu of data?.pendingUpdates || []) {
      await processMutation.mutateAsync(pu.id);
    }
  };

  // Handle skip entire batch
  const handleSkipBatch = () => {
    message.info('Skipping batch, moving to next...');
    setFieldStates(new Map());
    setSkipBlockedAction(false);
    refetch();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="pending-updates-review" style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading pending updates...</div>
      </div>
    );
  }

  // Error or no data state
  if (error || !data) {
    return (
      <div className="pending-updates-review" style={{ padding: 24 }}>
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" align="center">
                <Title level={4}>No Updates to Review</Title>
                <Text type="secondary">
                  All document updates have been processed. Check back later when new documents are received.
                </Text>
              </Space>
            }
          >
            <Button type="primary" onClick={() => navigate('/')}>
              Return to Dashboard
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  // Get context info
  const shipmentRef = data.shipment?.reference || data.contract?.reference || 'Unknown';
  const contractRef = data.shipment?.contract?.reference || data.contract?.reference;
  const buyer = data.shipment?.contract?.buyer || data.contract?.buyer;
  const supplier = data.shipment?.contract?.supplier || data.contract?.supplier;
  const blockedAction = data.pendingUpdates[0]?.blockedAction;
  const sourceDocumentUrl = data.pendingUpdates.find(pu => pu.sourceDocumentUrl)?.sourceDocumentUrl;

  return (
    <div className="pending-updates-review" style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* Inject custom styles for decision buttons */}
      <style>{decisionButtonStyles}</style>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ marginBottom: 8 }}>
            <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
            Data Confirmation Required
          </Title>
          <Space wrap>
            <Tag color="blue">{shipmentRef}</Tag>
            {contractRef && contractRef !== shipmentRef && (
              <Tag color="cyan">Contract: {contractRef}</Tag>
            )}
            {supplier && <Tag>Supplier: {supplier.code || supplier.name}</Tag>}
            {buyer && <Tag>Buyer: {buyer.code || buyer.name}</Tag>}
            <Tag color="orange">{data.totalPending} total pending</Tag>
          </Space>
        </div>
        <Space>
          {sourceDocumentUrl && (
            <Button
              icon={<FileTextOutlined />}
              href={sourceDocumentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Document
            </Button>
          )}
        </Space>
      </div>

      {/* Blocked Action Warning */}
      {blockedAction && blockedAction !== 'NONE' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message={
            <Space>
              <Text strong>Pending Action:</Text>
              <Text>{BLOCKED_ACTION_LABELS[blockedAction] || blockedAction}</Text>
            </Space>
          }
          description={
            <Checkbox
              checked={skipBlockedAction}
              onChange={(e) => setSkipBlockedAction(e.target.checked)}
              style={{ marginTop: 8 }}
            >
              Skip this action (only apply data changes)
            </Checkbox>
          }
        />
      )}

      {/* Fields grouped by entity */}
      <Card style={{ marginBottom: 24 }}>
        {Array.from(groupedFields.entries()).map(([groupKey, fields], idx) => {
          // groupKey format: entityType:entityId
          const colonIdx = groupKey.indexOf(':');
          const entityType = groupKey.slice(0, colonIdx);
          const entityId = groupKey.slice(colonIdx + 1);
          return (
            <div key={groupKey}>
              {idx > 0 && <Divider />}
              <div style={{ marginBottom: 16 }}>
                <Tag color={entityType === 'CONTAINER' ? 'purple' : entityType === 'GOOD' ? 'green' : 'blue'}>
                  {getEntityLabel(entityType)}
                </Tag>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  ID: {entityId.slice(0, 8)}...
                </Text>
              </div>
              {fields.map((field) => {
                const key = getFieldKey(field.puId, field.entityType, field.entityId, field.fieldName);
                const state = fieldStates.get(key) || { decision: null, editedValue: undefined };
                return (
                  <FieldRow
                    key={key}
                    entityType={field.entityType}
                    entityId={field.entityId}
                    fieldName={field.fieldName}
                    currentValue={field.currentValue}
                    proposedValue={field.proposedValue}
                    state={state}
                    onChange={(decision, editedValue) =>
                      handleFieldChange(field.puId, field.entityType, field.entityId, field.fieldName, decision, editedValue)
                    }
                  />
                );
              })}
            </div>
          );
        })}
      </Card>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          icon={<RollbackOutlined />}
          onClick={handleSkipBatch}
          disabled={processMutation.isPending}
        >
          Skip Entire Batch
        </Button>
        <Space>
          <Text type="secondary">
            {fieldStates.size} of {allFields.length} fields decided
          </Text>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmit}
            disabled={!allFieldsDecided || processMutation.isPending}
            loading={processMutation.isPending}
          >
            Submit Changes
          </Button>
        </Space>
      </div>

      {/* Inline styles for field rows */}
      <style>{`
        .field-row {
          display: flex;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
          gap: 16px;
        }
        .field-row:last-child {
          border-bottom: none;
        }
        .field-label {
          width: 150px;
          flex-shrink: 0;
        }
        .field-values {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }
        .field-current,
        .field-proposed {
          display: flex;
          flex-direction: column;
          min-width: 120px;
          max-width: 200px;
        }
        .field-arrow {
          color: #999;
          font-size: 16px;
        }
        .field-actions {
          flex-shrink: 0;
        }
        .ant-radio-button-wrapper-checked {
          background-color: var(--ant-primary-color) !important;
        }
        .ant-radio-button-wrapper-checked[value="accept"] {
          background-color: #52c41a !important;
          border-color: #52c41a !important;
        }
        .ant-radio-button-wrapper-checked[value="reject"] {
          background-color: #ff4d4f !important;
          border-color: #ff4d4f !important;
        }
        .ant-radio-button-wrapper-checked[value="skip"] {
          background-color: #faad14 !important;
          border-color: #faad14 !important;
        }
      `}</style>
    </div>
  );
};

export default PendingUpdatesReview;
